//! Dependencies
import { API_URI } from "../../constants/consts";
import { api } from "../../services/apis/configs/axiosConfigs";

//* Reuse the question mapper so list questions match the questions domain
import { mapQuestionResponse } from "../questions/questionsAPI";

//* Types
import {
  type QuestionList,
  type QuestionListDetail,
} from "./types/types";

const BASE = `${API_URI}/admin/private/question-lists`;

function mapListResponse(data: any): QuestionList {
  return {
    id: String(data.id ?? ""),
    name: String(data.name ?? ""),
    itemsCount: Number(data.itemsCount ?? data.items_count ?? 0),
    createdAt: data.createdAt ? new Date(data.createdAt) : new Date(data.created_at ?? Date.now()),
  };
}

function mapListDetailResponse(data: any): QuestionListDetail {
  return {
    ...mapListResponse(data),
    questions: Array.isArray(data.questions)
      ? data.questions.map((q: any) => mapQuestionResponse(q))
      : [],
  };
}

/**
 * @Component questionListsAPI
 * @Description CRUD for the named question lists (favorites).
 */
export const questionListsAPI = {
  getAll: async function (token: string): Promise<QuestionList[]> {
    return api
      .request({
        url: BASE,
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const items = response.data?.data ?? [];
        return Array.isArray(items) ? items.map(mapListResponse) : [];
      })
      .catch((error) => {
        console.error("Error fetching question lists:", error);
        throw error;
      });
  },

  getById: async function (id: string, token: string): Promise<QuestionListDetail> {
    return api
      .request({
        url: `${BASE}/${id}`,
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => mapListDetailResponse(response.data.data))
      .catch((error) => {
        console.error("Error fetching question list:", error);
        throw error;
      });
  },

  create: async function (name: string, token: string): Promise<QuestionList> {
    return api
      .request({
        url: BASE,
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        data: { name },
      })
      .then((response) => mapListResponse(response.data.data))
      .catch((error) => {
        console.error("Error creating question list:", error);
        throw error;
      });
  },

  rename: async function (id: string, name: string, token: string): Promise<QuestionList> {
    return api
      .request({
        url: `${BASE}/${id}`,
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        data: { name },
      })
      .then((response) => mapListResponse(response.data.data))
      .catch((error) => {
        console.error("Error renaming question list:", error);
        throw error;
      });
  },

  remove: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${BASE}/${id}`,
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting question list:", error);
        throw error;
      });
  },

  addItem: async function (listId: string, questionId: string, token: string): Promise<void> {
    return api
      .request({
        url: `${BASE}/${listId}/items`,
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        data: { question_id: questionId },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error adding question to list:", error);
        throw error;
      });
  },

  removeItem: async function (listId: string, questionId: string, token: string): Promise<void> {
    return api
      .request({
        url: `${BASE}/${listId}/items/${questionId}`,
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error removing question from list:", error);
        throw error;
      });
  },
};
